import { useEffect, useRef } from 'react'

export const SESSION_STATUS = {
  EXPIRED: 1,
  ACTIVE: 100
}

const PING_URL = 'https://kalendarium.tasr.sk/public/index.php/ping'

/**
 * The keep-alive interval has to stay well below the shortest possible session
 * lifetime on the BE. Browsers throttle timers in background tabs (~1 minute
 * granularity, even more with intensive throttling) so a ping scheduled every
 * 4 minutes could easily fire 5+ minutes later and the cookie would be gone.
 */
export const PING_INTERVAL_MS = 120000

/**
 * Pings the BE. Never rejects - a network hiccup must not log the user out,
 * only an explicit "session expired" answer from the BE may do that.
 */
export const pingSession = () =>
  fetch(PING_URL, {
    mode: 'cors',
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    body: '',
    headers: { 'content-type': 'application/json' }
  })
    .then((response) => response.json())
    .then((data) => ({ code: data?.status?.code ?? null, data, failed: false }))
    .catch(() => ({ code: null, data: null, failed: true }))

/** Detects the "you are not logged in anymore" answer in any API response. */
export const isSessionExpiredResponse = (data) => data?.status?.code === SESSION_STATUS.EXPIRED

/**
 * Keeps the session cookie alive and detects an expired session.
 *
 * Besides the regular interval it also pings whenever the tab becomes visible
 * again, the window gets focus or the connection comes back - after a frozen /
 * throttled background tab or a sleeping device the interval cannot be trusted.
 */
export const useSessionKeepAlive = ({
  onSessionExpired,
  enabled = true,
  intervalMs = PING_INTERVAL_MS
}) => {
  const onSessionExpiredRef = useRef(onSessionExpired)
  const lastPingRef = useRef(Date.now())
  const pingInFlightRef = useRef(false)

  useEffect(() => {
    onSessionExpiredRef.current = onSessionExpired
  }, [onSessionExpired])

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    let cancelled = false

    const ping = () => {
      if (cancelled || pingInFlightRef.current) {
        return
      }

      pingInFlightRef.current = true
      pingSession().then(({ code, failed }) => {
        pingInFlightRef.current = false

        if (cancelled || failed) {
          return
        }

        lastPingRef.current = Date.now()

        if (code === SESSION_STATUS.EXPIRED) {
          onSessionExpiredRef.current?.()
        }
      })
    }

    const handleWakeUp = () => {
      if (document.visibilityState === 'hidden') {
        return
      }

      // the timer might have been throttled or frozen while we were in the background
      if (Date.now() - lastPingRef.current < intervalMs / 2) {
        return
      }

      ping()
    }

    const intervalId = setInterval(ping, intervalMs)
    document.addEventListener('visibilitychange', handleWakeUp)
    window.addEventListener('focus', handleWakeUp)
    window.addEventListener('online', handleWakeUp)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleWakeUp)
      window.removeEventListener('focus', handleWakeUp)
      window.removeEventListener('online', handleWakeUp)
    }
  }, [enabled, intervalMs])
}
